import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  Grid,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Stepper,
  Step,
  StepLabel,
  Chip,
} from '@weave-mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';

import LoadingSpinner from 'components/LoadingSpinner';
import PageTitle from 'components/PageTitle';
import WeaveMuiThemeProvider from 'components/WeaveMuiThemeProvider';
import SelectedTestsList from 'components/CreateTestSuite/SelectedTestsList';
import TestSuiteReviewForm from 'components/CreateTestSuite/TestSuiteReviewForm';
import SubcategoryTestList from 'components/CreateTestSuite/SubcategoryTestList';

import {
  getTestSuiteDetails,
  submitNewTestSuite,
  updateTestSuite,
  getEnvironments,
} from 'store/actions/test_suites_actions';
import {
  fetchCategoryDataQuarry,
  getHostingProjectTestCases,
  fetchChannelList,
  getHostingProjects,
} from 'utils/api.service';
import { transformTestData } from 'utils/transformTestDataUtils';

/**
 * CreateTestSuite - Main component for creating or editing test suites
 *
 * This component provides a two-panel interface:
 * - Left panel: Service/environment selection and category navigation
 * - Right panel: Selected tests list (selection view) or review form (review view)
 *
 * @returns {JSX.Element} The create test suite page
 */
function CreateTestSuite() {
  const history = useHistory();
  const { id: testSuiteId } = useParams();

  const dispatch = useDispatch();
  const categories = useSelector((state) => state.menu.categories);
  const userInfo = useSelector((state) => state.app.userInfo);

  // State management
  const [services, setServices] = useState([]);
  const [service, setService] = useState({});
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [loadingEnvironments, setLoadingEnvironments] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [environment, setEnvironment] = useState('');
  const [testSuite, setTestSuite] = useState({});
  const [pageType, setPageType] = useState('new');
  const [notificationEmail, setNotificationEmail] = useState('');

  // New state for redesign
  const [viewMode, setViewMode] = useState('selection'); // 'selection' | 'review'
  const [testSuiteName, setTestSuiteName] = useState('');
  const [description, setDescription] = useState('');
  const [notifyMe, setNotifyMe] = useState(true);
  const [notifyOthers, setNotifyOthers] = useState(false);
  const [updateJiraXray, setUpdateJiraXray] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedWorkstreams, setSelectedWorkstreams] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedSubcategory, setExpandedSubcategory] = useState(null);
  const [loadingSubcategoryId, setLoadingSubcategoryId] = useState(null);
  const [subcategoryTests, setSubcategoryTests] = useState({});

  /**
   * Filter and initialize services on mount
   */
  const filterServices = useCallback(() => {
    const validServices = ['thservices', 'projects'];
    const invalidCategories = ['testdata', 'Perf testing'];

    const filteredServices = validServices
      .reduce((accumulator, currentCategory) => {
        return [...accumulator, ...(categories[currentCategory] || [])];
      }, [])
      .filter((category) => !invalidCategories.includes(category.servicename))
      .map((category) => {
        if (!category.servicename) {
          category.servicename = category.name;
        }
        return category;
      });

    setServices(filteredServices);

    // If not editing, use first service
    if (!testSuiteId && filteredServices.length > 0) {
      setService(filteredServices[0]);
      return filteredServices[0];
    }

    return null;
  }, [categories, testSuiteId]);

  /**
   * Load environments for a given service
   */
  const loadEnvironments = useCallback((selectedService) => {
    if (!selectedService?.servicename) return;

    setLoadingEnvironments(true);
    getEnvironments(selectedService.servicename).then((response) => {
      setEnvironments(response.data.envlist);
      setEnvironment(response.data.envlist[0]?.env || '');
      setLoadingEnvironments(false);
    });
  }, []);

  /**
   * Load categories and subcategories for selected service
   */
  const onServiceSelected = useCallback(
    (selectedService, clearData = true, skipEnvironmentLoad = false) => {
      if (!selectedService?.servicename) return;

      setLoadingSubcategories(true);

      if (clearData) {
        setSelectedChannels([]);
        setSelectedWorkstreams([]);
        setSubcategoryTests({});
        setExpandedSubcategory(null);
      }

      if (selectedService.servicename === 'cjt') {
        const requests = selectedService.categories.map((c) =>
          fetchCategoryDataQuarry(c.categoryid)
        );

        Promise.all(requests).then((responses) => {
          const categoriesData = responses.map((response) => response.data);
          selectedService.categories = categoriesData;

          selectedService.categories.forEach((category) => {
            category.subcategory.forEach((subcategory) => {
              subcategory.categoryid = category.categoryid;
            });
          });

          if (!skipEnvironmentLoad) {
            loadEnvironments(selectedService);
          }

          setService({ ...selectedService });
          setLoadingSubcategories(false);
        });
      } else {
        const requests = selectedService.workstream.map((c) =>
          getHostingProjects(selectedService.org, c.workstreamname)
        );

        Promise.all(requests).then((responses) => {
          const categoriesData = responses.map((response) => response.data);

          selectedService.categories = categoriesData
            .map((category) => {
              if (category.project[0]) {
                const workstream = selectedService.workstream.find(
                  (w) => w.workstreamname === category.project[0].workstream
                );
                const subcategory = category.project.map((p) => ({
                  ...p,
                  subcategoryname: p.displayname,
                  subcategoryid: p.projectname || p.name,
                  projectname: p.projectname || p.name,
                  workstream: p.workstream,
                  org: selectedService.servicename,
                }));

                return {
                  categoryname: workstream.displayname,
                  categoryid: workstream.workstreamname,
                  subcategory,
                };
              }
              return null;
            })
            .filter((c) => c);

          setService({ ...selectedService });
          setLoadingSubcategories(false);
        });
      }
    },
    [loadEnvironments]
  );

  /**
   * Load existing test suite for editing
   */
  const loadExistingTestSuite = useCallback(() => {
    if (!testSuiteId) return;

    setPageType('edit');

    getTestSuiteDetails(testSuiteId).then((response) => {
      const foundService = services.find(
        (s) => s.servicename === response.data.category
      );

      if (!foundService) return;

      // Extract all test cases from payload.category[].subcategory[].testcases[]
      const testCases = [];

      if (response.data.payload?.category) {
        response.data.payload.category.forEach((category) => {
          if (category.subcategory) {
            category.subcategory.forEach((subcategory) => {
              if (
                subcategory.testcases &&
                Array.isArray(subcategory.testcases)
              ) {
                subcategory.testcases.forEach((testcase) => {
                  testCases.push({
                    testcaseid: testcase.testcaseid,
                    testcasename: testcase.testcasename || testcase.name,
                    name: testcase.name || testcase.testcasename,
                    projectname: testcase.projectname,
                    workstreamname: testcase.workstreamname,
                    subcategoryid: subcategory.subcategoryid,
                    subcategoryname: subcategory.subcategoryname,
                    categoryid: category.categoryid,
                    categoryname: category.categoryname,
                    workstreamdisplayname: category.categoryname,
                    environment: response.data.environment?.toLowerCase() || '',
                    channel: (
                      testcase.projectname ||
                      subcategory.subcategoryid ||
                      subcategory.subcategoryname ||
                      ''
                    ).toLowerCase(),
                    org: foundService.servicename,
                  });
                });
              }
            });
          }
        });
      }

      setTestSuite(response.data);
      setSelectedChannels(testCases);
      setNotificationEmail(response.data.payload.emailrecipients);
      setEnvironment(response.data.environment);
      setEnvironments([
        {
          displayname: response.data.environment,
          env: response.data.environment,
        },
      ]);
      setService(foundService);
      const initialWorkstreams = Array.from(
        new Set(
          (response.data.payload.category || [])
            .map((categoryItem) =>
              String(
                categoryItem.categoryid ||
                  categoryItem.categoryId ||
                  categoryItem.id ||
                  categoryItem.categoryname ||
                  ''
              )
            )
            .filter(
              (value) =>
                Boolean(value) && value.toLowerCase() !== 'uncategorized'
            )
        )
      );
      setSelectedWorkstreams(initialWorkstreams);
      setTestSuiteName(response.data.testsuitename || '');
      setDescription(response.data.payload?.description || '');

      // Extract tags from response (could be in response.data.tags or response.data.payload.tags)
      const tags = response.data.tags || response.data.payload?.tags || [];
      setSelectedTags(Array.isArray(tags) ? tags : []);

      // Skip environment load since we already have it from the test suite data
      onServiceSelected(foundService, false, true);

      // Load testdata for selected test cases to enable tag filtering
      // This needs to happen after onServiceSelected completes
      setTimeout(() => {
        loadTestDataForSelectedCases(
          testCases,
          foundService,
          response.data.environment
        );
      }, 500);
    });
  }, [testSuiteId, services, onServiceSelected]);

  const hasInitialized = useRef(false);
  const hasLoadedTestSuite = useRef(false);

  // Initialize on mount
  useEffect(() => {
    if (hasInitialized.current) {
      return;
    }

    const initialService = filterServices();
    if (initialService) {
      onServiceSelected(initialService);
      hasInitialized.current = true;
    }
  }, [filterServices, onServiceSelected]);

  // Load existing test suite if editing
  useEffect(() => {
    if (testSuiteId && services.length > 0 && !hasLoadedTestSuite.current) {
      hasLoadedTestSuite.current = true;
      loadExistingTestSuite();
    }
  }, [testSuiteId, services.length, loadExistingTestSuite]);

  /**
   * Handle service selection change
   */
  const handleServiceChange = useCallback(
    (e) => {
      const selectedService = services.find(
        (s) => s.servicename === e.target.value
      );

      if (selectedService?.servicename) {
        setService(selectedService);
        onServiceSelected(selectedService);
        loadEnvironments(selectedService);
      }
    },
    [services, onServiceSelected, loadEnvironments]
  );

  /**
   * Handle environment selection change
   */
  const handleEnvironmentChange = useCallback((e) => {
    setEnvironment(e.target.value);
  }, []);

  const handleWorkstreamsChange = useCallback((event) => {
    const { value } = event.target;
    const values = Array.isArray(value)
      ? value.map((item) => String(item))
      : value
      ? [String(value)]
      : [];

    setSelectedWorkstreams(values);
    setExpandedSubcategory(null);
    setSubcategoryTests({});
  }, []);

  const handleRemoveWorkstream = useCallback((workstreamId) => {
    setSelectedWorkstreams((prev) =>
      prev.filter((value) => value !== workstreamId)
    );
    setExpandedSubcategory(null);
    setSubcategoryTests({});
  }, []);

  const isTestcaseSelected = useCallback(
    (testcaseId) => selectedChannels.some((tc) => tc.testcaseid === testcaseId),
    [selectedChannels]
  );

  const handleTestcaseToggle = useCallback(
    (category, subcategory, testcase) => {
      const exists = selectedChannels.some(
        (tc) => tc.testcaseid === testcase.testcaseid
      );

      if (exists) {
        setSelectedChannels((prev) =>
          prev.filter((tc) => tc.testcaseid !== testcase.testcaseid)
        );
      } else {
        setSelectedChannels((prev) => [
          ...prev,
          {
            ...testcase,
            name: testcase.displayname || testcase.name,
            subcategoryid: testcase.projectname || testcase.subcategoryid,
            subcategoryname:
              testcase.projectdisplayname || subcategory.subcategoryname,
            categoryid: category.categoryid,
            categoryname: category.categoryname,
            workstreamname: category.categoryid,
            workstreamdisplayname: category.categoryname,
            environment: environment.toLowerCase(),
            channel: (
              testcase.projectname ||
              testcase.subcategoryid ||
              subcategory.subcategoryname
            ).toLowerCase(),
          },
        ]);
      }
    },
    [selectedChannels, environment]
  );

  const handleSelectAllTests = useCallback(
    (category, subcategory) => {
      const tests = subcategoryTests[subcategory.subcategoryid] || [];
      const newSelection = tests.filter(
        (test) =>
          !selectedChannels.some((tc) => tc.testcaseid === test.testcaseid)
      );

      if (newSelection.length === 0) {
        setSelectedChannels((prev) =>
          prev.filter(
            (tc) => tc.subcategoryname !== subcategory.subcategoryname
          )
        );
      } else {
        setSelectedChannels((prev) => [
          ...prev,
          ...newSelection.map((test) => ({
            ...test,
            name: test.displayname || test.name,
            subcategoryid: test.projectname || test.subcategoryid,
            subcategoryname:
              test.projectdisplayname || subcategory.subcategoryname,
            categoryid: category.categoryid,
            categoryname: category.categoryname,
            workstreamname: category.categoryid,
            workstreamdisplayname: category.categoryname,
            environment: environment.toLowerCase(),
            channel: (
              test.projectname ||
              test.subcategoryid ||
              subcategory.subcategoryname
            ).toLowerCase(),
          })),
        ]);
      }
    },
    [selectedChannels, subcategoryTests, environment]
  );

  const loadSubcategoryTests = useCallback(
    async (categoryId, subcategory) => {
      if (subcategoryTests[subcategory.subcategoryid]) {
        return;
      }

      setLoadingSubcategoryId(subcategory.subcategoryid);

      try {
        let tests = [];

        if (service.servicename === 'cjt') {
          const response = await fetchChannelList(
            categoryId,
            subcategory.subcategoryid
          );
          const data = response?.data || {};

          tests = (data.testcases || []).map((testcase) => ({
            ...testcase,
            subcategoryid: subcategory.subcategoryid,
            subcategoryname: subcategory.subcategoryname,
          }));
        } else {
          const response = await getHostingProjectTestCases(
            service.servicename,
            subcategory.workstream,
            subcategory.projectname
          );

          const results = response?.data?.results || [];
          tests = results.map((testcase) => ({
            ...testcase,
            testcaseid: testcase.id,
            testcasename: testcase.testcasename || testcase.name,
            subcategoryid: subcategory.subcategoryid,
            subcategoryname: subcategory.subcategoryname,
            projectname: subcategory.projectname,
            workstream: subcategory.workstream,
            workstreamname: subcategory.workstream,
            org: service.servicename,
          }));
        }

        // Apply test data transformation to handle testdata_<env> _tags format and other transformations
        const transformedTests = transformTestData(tests, false);

        setSubcategoryTests((prev) => ({
          ...prev,
          [subcategory.subcategoryid]: transformedTests,
        }));
      } catch (error) {
        console.error('Failed to load test cases for subcategory:', error);
        setSubcategoryTests((prev) => ({
          ...prev,
          [subcategory.subcategoryid]: [],
        }));
      } finally {
        setLoadingSubcategoryId(null);
      }
    },
    [service.servicename, subcategoryTests]
  );

  const handleSubcategoryToggle = useCallback(
    (categoryId, subcategory) => {
      const isAlreadyExpanded =
        expandedSubcategory?.subcategoryid === subcategory.subcategoryid;

      if (isAlreadyExpanded) {
        setExpandedSubcategory(null);
        return;
      }

      setExpandedSubcategory(subcategory);
      loadSubcategoryTests(categoryId, subcategory);
    },
    [expandedSubcategory, loadSubcategoryTests]
  );

  /**
   * Load testdata for selected test cases when editing
   */
  const loadTestDataForSelectedCases = useCallback(
    async (testCases, foundService, env) => {
      if (!testCases.length || !env) return;

      try {
        // Group test cases by subcategory to batch requests
        const testCasesBySubcategory = {};
        testCases.forEach((tc) => {
          const key = tc.subcategoryid || tc.projectname;
          if (!testCasesBySubcategory[key]) {
            testCasesBySubcategory[key] = {
              subcategoryid: tc.subcategoryid,
              subcategoryname: tc.subcategoryname,
              workstream: tc.workstreamname,
              projectname: tc.projectname,
              categoryid: tc.categoryid,
              testcases: [],
            };
          }
          testCasesBySubcategory[key].testcases.push(tc);
        });

        // Fetch test cases with testdata for each subcategory
        const requests = Object.values(testCasesBySubcategory).map(
          async (subcategory) => {
            try {
              let tests = [];
              if (foundService.servicename === 'cjt') {
                const response = await fetchChannelList(
                  subcategory.categoryid,
                  subcategory.subcategoryid
                );
                const data = response?.data || {};
                tests = data.testcases || [];
              } else {
                const response = await getHostingProjectTestCases(
                  foundService.servicename,
                  subcategory.workstream,
                  subcategory.projectname
                );
                const results = response?.data?.results || [];
                tests = results.map((testcase) => ({
                  ...testcase,
                  testcaseid: testcase.id,
                  testcasename: testcase.testcasename || testcase.name,
                }));
              }
              return { subcategory, tests };
            } catch (error) {
              console.error(
                'Failed to load test cases for subcategory:',
                error
              );
              return { subcategory, tests: [] };
            }
          }
        );

        const results = await Promise.all(requests);

        // Transform test data
        const allTests = results.flatMap((r) => r.tests);
        const transformedTests = transformTestData(allTests, false);

        // Merge testdata into selected test cases
        const testDataEnvKey = `testdata_${env.toLowerCase()}`;
        const mergedTestCases = testCases.map((testCase) => {
          const testCaseWithTestData = transformedTests.find(
            (test) => test.testcaseid === testCase.testcaseid
          );
          if (testCaseWithTestData) {
            const testData = testCaseWithTestData[testDataEnvKey] || [];
            return {
              ...testCase,
              [testDataEnvKey]: testData,
            };
          }
          return testCase;
        });

        setSelectedChannels(mergedTestCases);
      } catch (error) {
        console.error(
          'Failed to load testdata for selected test cases:',
          error
        );
      }
    },
    []
  );

  /**
   * Clear all selected channels
   */
  const handleClearAll = useCallback(() => {
    setSelectedChannels([]);
  }, []);

  /**
   * Remove a single test case
   */
  const handleRemoveTestCase = useCallback((testCase) => {
    setSelectedChannels((prev) =>
      prev.filter((c) => c.testcaseid !== testCase.testcaseid)
    );
  }, []);

  /**
   * Continue to review mode
   */
  const handleContinueToReview = useCallback(() => {
    setViewMode('review');
  }, []);

  /**
   * Handle form field changes in review mode
   */
  const handleFieldChange = useCallback((field, value) => {
    switch (field) {
      case 'testSuiteName':
        setTestSuiteName(value);
        break;
      case 'description':
        setDescription(value);
        break;
      case 'notifyMe':
        setNotifyMe(value);
        break;
      case 'notifyOthers':
        setNotifyOthers(value);
        break;
      case 'notificationEmail':
        setNotificationEmail(value);
        break;
      case 'updateJiraXray':
        setUpdateJiraXray(value);
        break;
      case 'selectedTags':
        setSelectedTags(Array.isArray(value) ? value : []);
        break;
      default:
        break;
    }
  }, []);

  /**
   * Handle test suite submission
   */
  const handleSubmit = useCallback(() => {
    if (!testSuiteName.trim() || selectedChannels.length === 0) {
      return;
    }

    setIsSubmitting(true);

    // Group selected channels by category and subcategory
    const groupedCategories = {};

    selectedChannels.forEach((testCase) => {
      // Determine category ID and name - following TestSelection.js pattern
      const categoryId = testCase.categoryid || testCase.workstreamname || '';
      const categoryName =
        testCase.category || testCase.workstreamdisplayname || '';

      if (!groupedCategories[categoryId]) {
        groupedCategories[categoryId] = {
          categoryid: categoryId,
          categoryname: categoryName,
          subcategory: {},
        };
      }

      // Determine subcategory ID and name - following TestSelection.js pattern
      const subcategoryId =
        testCase.subcategoryid || testCase.projectname || '';
      const subcategoryName =
        testCase.projectdisplayname || testCase.subcategoryname || '';

      if (!groupedCategories[categoryId].subcategory[subcategoryId]) {
        groupedCategories[categoryId].subcategory[subcategoryId] = {
          subcategoryid: subcategoryId,
          subcategoryname: subcategoryName,
          testcases: [],
        };
      }

      groupedCategories[categoryId].subcategory[subcategoryId].testcases.push(
        testCase
      );
    });

    // Convert to array format
    const categoryArray = Object.values(groupedCategories).map((category) => ({
      categoryid: category.categoryid,
      categoryname: category.categoryname,
      subcategory: Object.values(category.subcategory),
    }));

    // Ensure tags are an array of strings
    const tagsArray = Array.isArray(selectedTags)
      ? selectedTags.map((tag) =>
          typeof tag === 'string' ? tag : tag.value || tag
        )
      : [];

    const payload = {
      testsuitename: testSuiteName.trim(),
      environment: environment,
      service: service.servicename,
      category: categoryArray,
      emailrecipients: notificationEmail,
      tags: tagsArray,
    };

    // Include description if provided
    if (description.trim()) {
      payload.description = description.trim();
    }

    const submitAction =
      pageType === 'new'
        ? dispatch(submitNewTestSuite(service.servicename, payload))
        : dispatch(updateTestSuite(testSuite.id, payload));

    Promise.resolve(submitAction)
      .then(() => {
        setIsSubmitting(false);
        history.push('/testsuites');
      })
      .catch((error) => {
        console.error('Failed to submit test suite:', error);
        setIsSubmitting(false);
      });
  }, [
    testSuiteName,
    description,
    notificationEmail,
    selectedChannels,
    selectedTags,
    pageType,
    service,
    environment,
    testSuite,
    dispatch,
    history,
  ]);

  /**
   * Handle exit action
   */
  const handleExit = useCallback(() => {
    history.push('/testsuites');
  }, [history]);

  /**
   * Handle cancel action (go back to selection view)
   */
  const handleCancel = useCallback(() => {
    setViewMode('selection');
  }, []);

  const serviceOptions = useMemo(
    () =>
      services.map((s) => ({
        value: s.servicename,
        label: s.displayName,
      })),
    [services]
  );

  const workstreamOptions = useMemo(() => {
    return (service.categories || []).map((category) => ({
      value: String(category.categoryid || category.id),
      label: category.categoryname || category.displayname,
    }));
  }, [service.categories]);

  const environmentOptions = useMemo(
    () =>
      environments.map((env) => ({
        value: env.env,
        label: env.displayname,
      })),
    [environments]
  );

  const renderServiceValue = (selected) => {
    if (!selected) {
      return (
        <Typography variant="body2" color="text.secondary">
          Select a service
        </Typography>
      );
    }

    const option = serviceOptions.find((item) => item.value === selected);
    return option ? option.label : selected;
  };

  const renderEnvironmentValue = (selected) => {
    if (!selected) {
      return (
        <Typography variant="body2" color="text.secondary">
          Select env
        </Typography>
      );
    }

    const option = environmentOptions.find((item) => item.value === selected);
    return option ? option.label : selected;
  };

  const renderServiceEnvironmentSelectors = () => {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select services, categories and environments to view available test
          cases
        </Typography>

        {/* First row: Services and Environment */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{ mb: 1, fontWeight: 'medium', color: 'text.secondary' }}
            >
              Services
            </Typography>
            <FormControl fullWidth size="small" variant="outlined">
              <Select
                value={service.servicename || ''}
                onChange={handleServiceChange}
                displayEmpty
                variant="outlined"
                renderValue={renderServiceValue}
                disabled={pageType === 'edit'}
                sx={{
                  '& .MuiSelect-select': {
                    height: '40px !important',
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                <MenuItem value="">
                  <Typography variant="body2" color="text.secondary">
                    Select a service
                  </Typography>
                </MenuItem>
                {serviceOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{ mb: 1, fontWeight: 'medium', color: 'text.secondary' }}
            >
              Environment
            </Typography>
            <FormControl fullWidth size="small" variant="outlined">
              <Select
                value={environment}
                onChange={handleEnvironmentChange}
                displayEmpty
                variant="outlined"
                renderValue={renderEnvironmentValue}
                disabled={
                  loadingEnvironments ||
                  !environments.length ||
                  pageType === 'edit'
                }
                sx={{
                  '& .MuiSelect-select': {
                    height: '40px !important',
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                <MenuItem value="">
                  <Typography variant="body2" color="text.secondary">
                    Select env
                  </Typography>
                </MenuItem>
                {environmentOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Second row: Categories (full width) */}
        <Box>
          <Typography
            variant="caption"
            sx={{ mb: 1, fontWeight: 'medium', color: 'text.secondary' }}
          >
            Categories
          </Typography>
          <FormControl
            fullWidth
            size="small"
            variant="outlined"
            disabled={!service.servicename || !workstreamOptions.length}
          >
            <Select
              multiple
              value={selectedWorkstreams}
              onChange={handleWorkstreamsChange}
              placeholder="Select categories"
              displayEmpty
              variant="outlined"
              renderValue={(selected) => {
                if (!selected.length) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      Select categories
                    </Typography>
                  );
                }

                const labels = workstreamOptions
                  .filter((option) => selected.includes(option.value))
                  .map((option) => option.label);
                return (
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      pr: 1,
                      fontWeight: '600 !important',
                    }}
                    noWrap
                  >
                    {labels.join(', ')}
                  </Typography>
                );
              }}
              sx={{
                '& .MuiSelect-select': {
                  height: '40px !important',
                  display: 'flex',
                  alignItems: 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                },
              }}
            >
              {workstreamOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
    );
  };

  /**
   * Render categories with accordions
   */
  const renderCategories = () => {
    if (loadingSubcategories || !environment) {
      return <LoadingSpinner text="Loading channels" />;
    }

    if (!service.categories || service.categories.length === 0) {
      return null;
    }

    const categoriesToRender = service.categories.filter((category) => {
      if (selectedWorkstreams.length === 0) {
        return true;
      }

      const categoryId = String(category.categoryid || category.id);
      return selectedWorkstreams.includes(categoryId);
    });

    if (!categoriesToRender.length) {
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 2, display: 'block' }}
        >
          Select one or more categories to view their test cases.
        </Typography>
      );
    }

    // Flatten all subcategories from the selected categories
    const allSubcategories = categoriesToRender.reduce((acc, category) => {
      if (Array.isArray(category.subcategory)) {
        const subcategories = category.subcategory.map((sub) => ({
          ...sub,
          parentCategoryId: category.categoryid || category.id,
          parentCategoryName: category.categoryname || category.displayname,
        }));
        return [...acc, ...subcategories];
      }
      return acc;
    }, []);

    return (
      <Box sx={{ mt: 2 }}>
        {selectedWorkstreams.length > 0 && (
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            {selectedWorkstreams
              .map((workstreamId) =>
                workstreamOptions.find(
                  (option) => option.value === workstreamId
                )
              )
              .filter(Boolean)
              .map((option) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  onDelete={() => handleRemoveWorkstream(option.value)}
                  size="small"
                  deleteIcon={<CloseIcon sx={{ width: 24, height: 24 }} />}
                  sx={{
                    minWidth: 70,
                    height: 24,
                    fontSize: '12px',
                    fontWeight: 600,
                    borderRadius: '4px',
                  }}
                />
              ))}
          </Box>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1, display: 'block' }}
        >
          Drag or click on test cases from a category to add to build your test
          suite
        </Typography>

        {allSubcategories.length > 0 ? (
          allSubcategories.map((subcategory) => {
            const isExpanded =
              expandedSubcategory?.subcategoryid === subcategory.subcategoryid;

            return (
              <Box
                key={`${subcategory.parentCategoryId}-${subcategory.subcategoryid}`}
                sx={{
                  mb: '4px',
                }}
              >
                <Accordion
                  disableGutters
                  elevation={0}
                  expanded={isExpanded}
                  sx={{
                    border: '1px solid #E5E5E5',
                    '&:before': {
                      display: 'none',
                    },
                  }}
                  onChange={() =>
                    handleSubcategoryToggle(
                      subcategory.parentCategoryId,
                      subcategory
                    )
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        width: '100%',
                        gap: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {subcategory.parentCategoryName}
                      </Typography>
                      <Typography
                        variant="headline-smaller"
                        sx={{ fontWeight: 700 }}
                      >
                        {subcategory.subcategoryname}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <SubcategoryTestList
                      category={{
                        categoryid: subcategory.parentCategoryId,
                        categoryname: subcategory.parentCategoryName,
                      }}
                      subcategory={subcategory}
                      testcases={
                        subcategoryTests[subcategory.subcategoryid] || []
                      }
                      loading={
                        loadingSubcategoryId === subcategory.subcategoryid
                      }
                      onToggleTest={handleTestcaseToggle}
                      isSelected={isTestcaseSelected}
                      onSelectAll={() =>
                        handleSelectAllTests(
                          {
                            categoryid: subcategory.parentCategoryId,
                            categoryname: subcategory.parentCategoryName,
                          },
                          subcategory
                        )
                      }
                    />
                  </AccordionDetails>
                </Accordion>
              </Box>
            );
          })
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ p: 2, display: 'block' }}
          >
            No projects available for the selected categories.
          </Typography>
        )}
      </Box>
    );
  };

  const title =
    pageType === 'new' ? 'Create a new test suite' : 'Edit test suite';
  const isReviewMode = viewMode === 'review';

  return (
    <WeaveMuiThemeProvider>
      <Box sx={{ px: 3, py: 2, pb: 6 }}>
        <PageTitle title={title} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Stepper
            activeStep={viewMode === 'review' ? 1 : 0}
            completedStep={viewMode === 'review' ? 1 : 0}
            orientation="horizontal"
            labelPlacement="bottom"
            variation="numeric"
            sx={{ width: '100%' }}
          >
            <Step>
              <StepLabel>Add tests</StepLabel>
            </Step>
            <Step>
              <StepLabel>Review tests</StepLabel>
            </Step>
          </Stepper>
        </Box>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Left Panel - Service Selection & Categories */}
          {!isReviewMode && (
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 3,
                  bgcolor: 'background.paper',
                  minHeight: 720,
                  maxHeight: 720,
                  overflowY: 'auto',
                }}
              >
                {renderServiceEnvironmentSelectors()}
                {renderCategories()}
              </Box>
            </Grid>
          )}

          {/* Right Panel - Selected Tests or Review Form */}
          <Grid item xs={12} md={isReviewMode ? 12 : 6}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 3,
                bgcolor: 'background.paper',
                minHeight: 720,
                maxHeight: 720,
                overflowY: 'auto',
                display: 'grid',
              }}
            >
              {isReviewMode ? (
                <TestSuiteReviewForm
                  testCases={selectedChannels}
                  testSuiteName={testSuiteName}
                  description={description}
                  notifyMe={notifyMe}
                  notifyOthers={notifyOthers}
                  notificationEmail={notificationEmail}
                  updateJiraXray={updateJiraXray}
                  selectedTags={selectedTags}
                  environment={environment}
                  onFieldChange={handleFieldChange}
                  onSubmit={handleSubmit}
                  onExit={handleExit}
                  onCancel={handleCancel}
                  onRemove={handleRemoveTestCase}
                  isSubmitting={isSubmitting}
                  userEmail={userInfo?.emailid || ''}
                  pageType={pageType}
                />
              ) : (
                <SelectedTestsList
                  testCases={selectedChannels}
                  onRemove={handleRemoveTestCase}
                  onContinue={handleContinueToReview}
                  onClearAll={handleClearAll}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </WeaveMuiThemeProvider>
  );
}

export default CreateTestSuite;
